import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, AlertCircle, ArrowRight, MessageCircle, RefreshCw, CheckCircle } from 'lucide-react';

const CheckoutPending = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checking, setChecking] = useState(false);
  const [lastCheck, setLastCheck] = useState(null);
  
  const paymentId = searchParams.get('payment_id') || searchParams.get('external_reference');

  const checkPaymentStatus = async (forceCheck = false) => {
    if (!paymentId) {
      console.error('❌ Payment ID not found in URL');
      return;
    }

    try {
      setChecking(true);
      console.log('🔍 Verificando status do pagamento:', paymentId, forceCheck ? '(FORÇADO)' : '');
      
      // Se é verificação forçada, usa o endpoint mais agressivo
      const endpoint = forceCheck 
        ? `${process.env.REACT_APP_BACKEND_URL}/api/checkout/force-check/${paymentId}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/checkout/status/${paymentId}`;
      
      const response = await fetch(endpoint, {
        method: forceCheck ? 'POST' : 'GET'
      });
      const data = await response.json();
      
      console.log('📥 Status do pagamento:', data);
      setLastCheck(new Date());
      
      if (data.status === 'approved') {
        console.log('✅ Pagamento aprovado! Redirecionando...');
        // Redirecionamento silencioso - sem notificações chatas
        navigate('/checkout/success');
        return true;
      }
      
      // Se está forçando a verificação e não foi aprovado, tenta novamente em 5 segundos
      if (forceCheck && data.status !== 'approved') {
        console.log('🔄 Tentando novamente em 5 segundos...');
        // Removido alert chato - usuário já vê status na tela
        await new Promise(resolve => setTimeout(resolve, 5000));
        return await checkPaymentStatus(true);
      }
      
      return false;
    } catch (error) {
      console.error('❌ Erro ao verificar status:', error);
      // Removido alert de erro - usuário já vê mensagens na tela
      return false;
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Verificar imediatamente ao carregar
    if (paymentId) {
      checkPaymentStatus();
      
      // Configurar polling a cada 10 segundos
      const interval = setInterval(() => {
        checkPaymentStatus();
      }, 10000); // 10 segundos
      
      return () => clearInterval(interval);
    }
  }, [paymentId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Pending Card */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-slate-700 rounded-3xl p-8 md:p-12 text-center">
          {/* Pending Icon */}
          <div className="w-20 h-20 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-12 h-12 text-yellow-500" />
          </div>

          <h1 className="text-3xl md:text-4xl font-black text-white mb-4">
            Pagamento Pendente ⏳
          </h1>

          <p className="text-xl text-gray-300 mb-4">
            Seu pagamento ainda não foi confirmado
          </p>

          {/* Status Indicator */}
          {checking ? (
            <div className="flex items-center justify-center gap-2 text-cyan-400 mb-6">
              <RefreshCw className="w-5 h-5 animate-spin" />
              <span className="text-sm">Verificando pagamento...</span>
            </div>
          ) : lastCheck ? (
            <div className="flex items-center justify-center gap-2 text-gray-400 mb-6">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs">
                Última verificação: {lastCheck.toLocaleTimeString('pt-BR')}
              </span>
            </div>
          ) : null}

          <p className="text-sm text-gray-400 mb-8">
            ✨ Verificação automática a cada 10 segundos
          </p>

          {/* Alert Box */}
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6 mb-8 text-left">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-bold mb-2">Aguardando Confirmação</h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  Seu pagamento está em análise. Isso pode acontecer por alguns motivos:
                </p>
                <ul className="space-y-2">
                  <li className="text-gray-300 text-sm">• <strong>PIX:</strong> O QR Code foi gerado mas ainda não foi pago</li>
                  <li className="text-gray-300 text-sm">• <strong>Cartão:</strong> Pagamento em análise pelo banco</li>
                  <li className="text-gray-300 text-sm">• <strong>Processamento:</strong> Aguardando confirmação do Mercado Pago</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-2xl p-6 mb-8 text-left">
            <div className="flex items-start gap-3">
              <MessageCircle className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-white font-bold mb-2">O que acontece agora?</h3>
                <p className="text-gray-300 text-sm leading-relaxed mb-3">
                  Assim que o pagamento for aprovado, você receberá automaticamente no WhatsApp:
                </p>
                <ul className="space-y-2">
                  <li className="text-gray-300 text-sm">✅ Email de login</li>
                  <li className="text-gray-300 text-sm">✅ Chave de acesso única</li>
                  <li className="text-gray-300 text-sm">✅ Link direto para a plataforma</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8 text-left">
            <h3 className="text-white font-bold mb-4">Próximos Passos:</h3>
            <ol className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  1
                </span>
                <span className="text-gray-300 text-sm">
                  Se escolheu PIX, finalize o pagamento escaneando o QR Code
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  2
                </span>
                <span className="text-gray-300 text-sm">
                  Aguarde a confirmação (pode levar alguns minutos)
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
                  3
                </span>
                <span className="text-gray-300 text-sm">
                  Verifique seu WhatsApp para receber as credenciais
                </span>
              </li>
            </ol>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-4 mb-4">
            {/* Botão Já Fiz o Pagamento */}
            <button
              onClick={() => checkPaymentStatus(true)}
              disabled={checking}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
            >
              {checking ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  <span>Verificando Pagamento...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  <span>✅ Já Fiz o Pagamento PIX</span>
                </>
              )}
            </button>

            {/* Outros botões */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/checkout')}
                className="flex-1 py-4 bg-gradient-to-r from-cyan-500 to-cyan-600 text-white font-bold rounded-xl hover:from-cyan-600 hover:to-cyan-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
              >
                <span>Tentar Novamente</span>
                <ArrowRight className="w-5 h-5" />
              </button>

              <button
                onClick={() => navigate('/')}
                className="flex-1 py-4 bg-slate-800 border border-slate-700 text-white font-bold rounded-xl hover:bg-slate-700 transition-all"
              >
                Voltar ao Início
              </button>
            </div>
          </div>

          {/* Support */}
          <div className="mt-8 pt-6 border-t border-slate-700">
            <p className="text-gray-400 text-sm">
              Precisa de ajuda?{' '}
              <a
                href="https://wa.me/5511916560595"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cyan-400 hover:text-cyan-300 font-medium"
              >
                Entre em contato pelo WhatsApp
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPending;
